<template>
  <b-modal
    v-model="show"
    centered
    size="md"
    @show="onShow"
    :cancel-disabled="true"
  >
    <template #modal-title>
      <h3 class="font-weight-bold">
        {{ modalTitle }}
      </h3>
    </template>

    <div v-if="isLoading" class="text-center py-5">
      <digger-loader />
    </div>

    <template v-if="!isLoading && file">
      <b-row>
        <b-col>
          <table class="file-info">
            <tr>
              <td>File Name:</td>
              <td>{{ file.name }}</td>
            </tr>
            <tr v-for="info in visibleInfoTypes" :key="info.id">
              <td>{{ info.label }}:</td>
              <td>
                {{
                  info.field_type === 'date'
                    ? dateFormat(info.value)
                    : info.value
                }}
              </td>
            </tr>
            <tr>
              <td>TP Verification:</td>
              <td>{{ file.status }}</td>
            </tr>
            <tr v-if="companyApprovalLabel">
              <td>Company Approval:</td>
              <td>{{ companyApprovalLabel }}</td>
            </tr>
          </table>
        </b-col>
      </b-row>

      <b-row class="attachments-row my-4">
        <b-col class="file-attachments-col">
          <template v-if="file.files.length > 0">
            <p class="font-weight-bold opacity-100">Uploaded attachments*</p>
            <div class="file-attachments-scroll mb-2">
              <div class="d-flex flex-nowrap" style="gap: 16px">
                <a
                  v-for="file in file.files"
                  :key="file.id"
                  :href="file.url"
                  target="_blank"
                  class="file-attachment-link flex-shrink-0"
                  data-test="attachment-link"
                  @click.prevent="openPreview(file)"
                >
                  <img
                    class="file-img"
                    :src="attachmentSrc(file)"
                    @error="(e) => (e.target.src = file.url)"
                  />
                </a>
              </div>
            </div>
            *click on the file to preview it.
          </template>
          <template v-else>
            <p class="font-weight-bold opacity-100">File images not found.</p>
          </template>
        </b-col>
      </b-row>
    </template>

    <template #modal-footer>
      <template v-if="!isLoading && file">
        <div
          v-if="!readOnly"
          class="d-flex w-100 justify-content-between view-file-footer"
          style="gap: 12px"
        >
          <b-button
            v-if="canDeleteTradieFile && !file.deletion_scheduled_at"
            variant="danger"
            :disabled="!canDeleteFile"
            @click="onDeletingFile"
          >
            DELETE<span class="d-none d-sm-inline"> FILE</span>
          </b-button>
          <template v-if="showBothViewAndEdit">
            <b-button variant="outline-primary" @click="onViewFile">
              VIEW<span class="d-none d-sm-inline"> FILE</span>
            </b-button>
            <b-button variant="primary" @click="onEditFile">
              EDIT<span class="d-none d-sm-inline"> FILE</span>
            </b-button>
          </template>
          <b-button
            v-else-if="canViewTradieFile"
            variant="primary"
            @click="canEditFile ? onEditFile() : onViewFile()"
            >{{ canEditFile ? 'EDIT' : 'VIEW'
            }}<span class="d-none d-sm-inline"> FILE</span></b-button
          >
        </div>
        <b-button v-else variant="primary" class="ml-auto" @click="onViewFile">
          VIEW FILE
        </b-button>
      </template>
      <div v-else></div>
    </template>
    <ConfirmationModal
      v-if="file"
      :showModal="isDeletingFile"
      title="File Deletion"
      :message="`Are you sure to delete ${file.name}?`"
      ok-label="YES, DELETE FILE"
      ok-variant="danger"
      @close="isDeletingFile = false"
      @ok="deleteThisFile"
    />
    <FilePreviewModal
      v-if="previewAttachment"
      :visible="!!previewAttachment"
      :url="previewAttachment.url"
      :mime-type="previewAttachment.mime_type"
      :name="previewAttachment.file_name"
      :media-id="previewAttachment.id"
      :download-name="previewDownloadName"
      @close="previewAttachment = null"
    />
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ConfirmationModal from './ConfirmationModal.vue'
import FilePreviewModal from './FilePreviewModal.vue'
import {
  canDeleteFile as computeCanDeleteFile,
  canViewerEditFile,
} from '@/utils/filePermissions'
import { nonImageDocIcon } from '@/utils/fileTypeIcons'
import { visibleFileInformationTypes } from '@/utils/fileLabels'

const COMPANY_APPROVAL_LABEL = {
  approved: 'Approved',
  pending: 'Pending',
  rejected: 'Rejected',
  not_applicable: 'Archived',
}

export default {
  name: 'ViewFileModal',

  components: {
    ConfirmationModal,
    FilePreviewModal,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },

    fileId: {
      type: Number,
      required: true,
    },

    title: String,

    tradieId: {
      type: [Number, String],
      default: null,
    },

    readOnly: {
      type: Boolean,
      default: false,
    },

    shareId: {
      type: [Number, String],
      default: null,
    },

    permissionPrefix: {
      type: String,
      default: 'tradies.files',
    },

    /**
     * The project this file is being viewed under, when there is one (e.g.
     * opened from the Projects tab's tradie file table). Threaded through to
     * getFile (so company_approval reflects this project's decision, not a
     * roll-up across every project) and to "VIEW FILE"'s route so
     * ViewFilePage.vue can do the same and send project_id back on any
     * approve/reject/archive action taken from there.
     */
    projectId: {
      type: [Number, String],
      default: null,
    },

    /**
     * Tags the "View full file" route with where this modal was opened from,
     * so ViewFilePage's Back button can return there. Purely a routing hint —
     * unrelated to permissionPrefix, which drives real permission checks.
     */
    fromContext: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      isLoading: false,
      isDeletingFile: false,
      previewAttachment: null,
    }
  },

  computed: {
    ...mapGetters('file', ['file']),
    ...mapGetters('auth', ['user', 'hasPermission']),
    ...mapGetters('company', ['tradie', 'company']),
    // Drops the retired "Consent" information type (see fileLabels).
    visibleInfoTypes() {
      return visibleFileInformationTypes(
        this.file && this.file.file_information_types,
      )
    },
    ...mapGetters('project', {
      projectTradie: 'tradie',
    }),

    computedTradieId() {
      if (this.file.user && this.file.user.id) return this.file.user.id

      return this.$route.params.tradieId || null
    },

    show: {
      get() {
        return this.showModal
      },
      set() {
        this.$emit('close')
      },
    },

    canDeleteFile() {
      return computeCanDeleteFile(this.file, this.user)
    },

    canEditFile() {
      return canViewerEditFile(this.file, this.user, this.hasPermission)
    },

    /**
     * ViewFileModal only ever shows tradie files, so deletion by a business
     * admin requires the tradies.files.delete permission (the "company-only +
     * private" limit is enforced by the backend's `deletable` flag). Tradies
     * acting on their own files are not gated.
     */
    isBusinessAdmin() {
      return this.user && this.user.user_type === 'business_admin'
    },
    canDeleteTradieFile() {
      return (
        !this.isBusinessAdmin ||
        this.hasPermission(`${this.permissionPrefix}.delete`)
      )
    },

    canViewTradieFile() {
      return (
        !this.isBusinessAdmin ||
        this.hasPermission(`${this.permissionPrefix}.view`)
      )
    },

    /**
     * Everywhere else, View/Edit collapse into one button (Edit implies you
     * can already see everything View would show) — but business admins
     * looking at a tradie's file from the Projects tab or the Tradies tab
     * ('projects.tradie.files' / the default 'tradies.files' permissionPrefix)
     * get both as separate actions.
     */
    showBothViewAndEdit() {
      return (
        this.isBusinessAdmin &&
        ['projects.tradie.files', 'tradies.files'].includes(
          this.permissionPrefix,
        ) &&
        this.canViewTradieFile &&
        this.canEditFile
      )
    },

    // "Medical.pdf" — the file's own name plus the attachment's real extension
    // (the stored file_name can be a storage hash).
    previewDownloadName() {
      const attachment = this.previewAttachment
      if (!attachment) return ''
      const source = attachment.file_name || ''
      const dot = source.lastIndexOf('.')
      let ext = dot >= 0 ? source.slice(dot).toLowerCase() : ''
      const mimeExt = {
        'application/pdf': ['.pdf'],
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg'],
      }[attachment.mime_type]
      if (mimeExt && !mimeExt.includes(ext)) ext = mimeExt[0]
      const base = (this.file && this.file.name) || 'file'
      return `${base}${ext}`
    },

    companyApprovalLabel() {
      const status = this.file && this.file.company_approval
      return COMPANY_APPROVAL_LABEL[status] || ''
    },

    modalTitle() {
      // if Title prop provided
      console.log(this.file)
      if (this.title) {
        return this.title
      }
      // if FIle belongs to current user show my file
      if (this.file && this.file.user && this.file.user.mine) {
        return 'My File'
      }
      // otherwise return Tradies file
      return "Tradie's File"
    },
  },

  methods: {
    ...mapActions('file', {
      getFile: 'getFile',
    }),

    ...mapActions('project', {
      deleteFile: 'deleteFile',
    }),

    // Show the attachment in the preview dialog instead of a new browser tab.
    openPreview(attachment) {
      this.previewAttachment = attachment
    },

    onShow() {
      this.previewAttachment = null
      this.isLoading = true
      const payload =
        this.projectId != null
          ? { id: this.fileId, projectId: this.projectId }
          : this.fileId
      this.getFile(payload)
        .catch((err) => this.$emit('error', err))
        .finally(() => (this.isLoading = false))
    },

    onViewFile() {
      this.$emit('close')

      const route = { name: 'file.view-page', params: { id: this.fileId } }
      if (this.readOnly && this.shareId != null && this.tradieId != null) {
        route.query = {
          shared: '1',
          shareId: String(this.shareId),
          tradieId: String(this.tradieId),
        }
      } else if (this.permissionPrefix === 'projects.tradie.files') {
        // Marks the project-tab tradie context, so the file view shows the
        // company approval section only when reached from there. project_id
        // lets ViewFilePage.vue read/write that exact project's decision
        // instead of rolling up across every project this company has an
        // opinion on for the file.
        route.query = { from: 'project-tradie' }
        if (this.projectId != null) {
          route.query.project_id = String(this.projectId)
        }
      } else if (this.fromContext) {
        route.query = { from: this.fromContext }
      }
      this.$router.push(route)
    },

    async onEditFile() {
      if (!this.canEditFile) return
      this.$emit('close')
      this.$router.push({
        name: 'file.edit-page',
        params: { id: this.fileId },
      })
    },

    onDeletingFile() {
      if (!this.canDeleteTradieFile) return
      this.isDeletingFile = true
    },

    deleteThisFile() {
      if (!this.canDeleteTradieFile) return
      this.isLoading = true

      this.deleteFile(this.file.id)
        .then((res) => this.$emit('deleted', this.file.id))
        .catch((err) => this.$emit('error', err))
        .finally(() => (this.isLoading = false))
    },

    dateFormat(dateString) {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },

    // CSV/XLSX never get a real thumb_url (no server-side rasterization
    // path for a spreadsheet — see fileTypeIcons.js), and file.url itself
    // isn't an image either, so falling through to it just renders broken.
    // Show the bundled type icon directly instead.
    attachmentSrc(file) {
      return nonImageDocIcon(file) || file.thumb_url || file.url
    },
  },
}
</script>

<style lang="scss" scoped>
.file-attachments-col {
  min-width: 0;
}

/* Up to 3 buttons can render here (Delete, View, Edit, when a business admin
 * on the Projects and Tradies tab has both view and edit permission). The
 * global .btn has wide (32px) side padding that pushes the third button off
 * the row at typical modal widths (and at in-between browser zoom levels),
 * so always shrink padding + font and let the buttons share the width evenly
 * — not just on small screens — keeping them on one row rather than wrapping. */
.view-file-footer {
  flex-wrap: wrap;
  row-gap: 8px;
  gap: 8px !important;

  .btn {
    flex: 1 1 0;
    min-width: 0;
    padding-left: 0.4rem;
    padding-right: 0.4rem;
    font-size: 0.8rem;
    white-space: nowrap;
  }
}

table.file-info {
  tr td:first-child {
    padding-right: 16px;
    font-weight: bold;
  }

  tr td {
    padding-bottom: 8px;
  }
}

.file-attachments-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}

.file-attachment-link {
  display: inline-block;
}

.file-img {
  width: 100px;
  height: 100px;
  background-color: lightgray;
  opacity: 0.8;
}

.file-img:hover {
  opacity: 1;
}

/* Phones: strip the modal down so a short read-only file summary doesn't
 * scroll — tighter chrome, smaller title/labels, a smaller thumbnail, and a
 * full-width action button. */
@media (max-width: 575.98px) {
  /deep/ .modal-dialog {
    margin: 0.5rem;
  }

  /deep/ .modal-header {
    padding: 0.75rem 1rem;
  }

  /deep/ .modal-body {
    padding: 0.75rem 1rem;
  }

  /deep/ .modal-footer {
    padding: 0.5rem 1rem;
  }

  h3.font-weight-bold {
    font-size: 1.15rem;
    margin-bottom: 0;
  }

  table.file-info tr td {
    padding-bottom: 4px;
    font-size: 0.85rem;
  }

  .attachments-row {
    margin-top: 0.6rem !important;
    margin-bottom: 0.25rem !important;
  }

  .file-attachments-col {
    font-size: 0.78rem;
  }

  .file-attachments-col p {
    margin-bottom: 0.35rem !important;
    font-size: 0.85rem;
  }

  .file-img {
    width: 76px;
    height: 76px;
  }

  /* Read-only "VIEW FILE" button: full-width, easy tap target. */
  /deep/ .modal-footer > .btn {
    width: 100%;
    margin: 0 !important;
  }
}
</style>
